import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import { withTranslation } from '@application/i18n/with_translation';
import { BadgeText, Button, ButtonArrange, Flex, Spacer } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Article } from '@component/article.component';
import { DataList } from '@component/data_list.component';
import { Title } from '@component/title.component';
import { TopBar } from '@component/top_bar.component';
import { getCompletedInformationGathering } from '@feature/information-gathering/information-gathering-navigation';
import { useRepresentablePatients } from '@feature/representation/use-representable-patients';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useBasket } from '../../hooks/use-basket';
import { useCreateNewMessage } from '../../hooks/use-create-new-message';
import { useMessagingInformationGathering } from '../../hooks/use-messaging-information-gathering';

export function ScreenStart() {
	const featureUseRepresentatives = useFeatureFlag('PatientWeb.UseRepresentatives');
	const { goToScreen, flowData, updateFlowSettings } = useCreateNewMessage();
	const informationGathering = useMessagingInformationGathering();
	const location = useLocation();
	const informationGatheringSubmissionId = getCompletedInformationGathering(location.state)?.submissionId;
	const { basket } = useBasket();
	const analyticCtx = useAnalyticContext();
	const { currentlyRepresentedPatient, isLoading } = useRepresentablePatients({
		enabled: featureUseRepresentatives,
	});
	const shouldShowWhoIsRespondent = featureUseRepresentatives && currentlyRepresentedPatient === null;

	function handleNext() {
		analyticCtx.trackEvent('Clicked', 'Next button');
		if (informationGatheringSubmissionId) {
			updateFlowSettings({
				respondentType: 'me',
				representativeNin: null,
				asAgentFor: null,
				access: [],
			});
			goToScreen('reason-for-visit');
			return;
		}

		if (shouldShowWhoIsRespondent) {
			goToScreen('who-is-respondent');
			return;
		}

		if (currentlyRepresentedPatient) {
			void informationGathering.continueAfterRespondent('representative', {
				nhsNumber: currentlyRepresentedPatient.IdentityNumber,
				dateOfBirth: currentlyRepresentedPatient.DateOfBirth ?? '',
			});
			return;
		}

		void informationGathering.continueAfterRespondent('self');
	}

	useEffect(() => {
		analyticCtx.service.trackPageView({
			customTitle: 'Create new message: Start',
		});
	}, [analyticCtx]);

	if (flowData.flowState !== 'accessible') {
		// The parent view gates rendering on flowData being accessible already.
		return null;
	}

	const flowDataValue = flowData.value;

	return (
		<>
			<TopBar.Content skipAbortDialog={basket == null} />

			<Flex grow={1} gap={tokens.spacing.x2}>
				<Flex grow={1} gap={tokens.spacing.x1}>
					<Title
						title={flowDataValue.screenStart.title ?? withTranslation('View.CreateNewMessage.ScreenStart.FallbackTitle')}
						titleSize='small'
					/>

					{flowDataValue.price ? (
						<DataList.Root>
							<DataList.Item label={withTranslation('Common.Label.Cost')}>
								<BadgeText variant='info'>{flowDataValue.price}</BadgeText>
							</DataList.Item>
						</DataList.Root>
					) : null}

					<Spacer vertical={tokens.spacing.x1_5}>
						<Article>
							{flowDataValue.screenStart.description ?? withTranslation('View.CreateNewMessage.ScreenStart.FallbackDescription')}
						</Article>
					</Spacer>
				</Flex>

				<ButtonArrange>
					<Button onClick={handleNext} loading={isLoading}>
						{withTranslation('PW.Common.Next')}
					</Button>
				</ButtonArrange>
			</Flex>
		</>
	);
}
